import { observer } from 'mobx-react-lite'
import {
  WizardBottomBar,
  WizardBottomBarPrimaryButton,
  WizardBottomBarSection,
  WizardBottomBarSpacer,
  WizardContentBody,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderTitle,
  WizardPageAnimatedContent,
  WizardPageSingleColumnRow,
} from '@/components/Wizard'
import styles from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/MeasureOne.module.css'
import { MeasureOneKicker } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneKicker'
import { ButtonGroup } from '@/components/ButtonGroup'
import { Button } from '@/components/Button'
import { useNavigate, useOutletContext } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { links } from '@/utils/link'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import type { MeasureItem } from '@/features/jobs/details/tax-type/info-gathering/measure-one/JobTaxDataImport'
import type { Id } from '@/infra/id'
import type { WellKnownDocumentTypeShortId } from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import {
  AnswerOption,
  AnswerOptions,
} from '@/features/jobs/questions/components/AnswerOptions'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { LabelChip } from '@/components/Chip'
import { MeasureOneSpinner } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneSpinner'

export default observer(function MeasureOneSelectDocumentsContainer() {
  const vm = useOutletContext<JobDetailsViewModel>()
  const taxVm = vm.typeVm as TaxJobDetailsViewModel
  const measureOneVm = taxVm.infoGatheringVm.measureOneVm

  const navigate = useNavigate()

  const onPress = async () => {
    await measureOneVm.importSelectedDocuments()

    void navigate(links.taxJobInfoGathering(taxVm.job.id))
  }

  return (
    <>
      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentHeader>
            <SelectDocumentsHeader />
          </WizardContentHeader>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>

      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentBody>
            <SelectDocumentsContainer
              requiredDocuments={measureOneVm.measureOneSupportedDataTypes}
              availableItems={measureOneVm.items}
              onSelectItem={measureOneVm.toggleItemSelection}
              selectedItems={measureOneVm.selectedItemIds}
              isLoading={measureOneVm.isLoadingItems}
            />
          </WizardContentBody>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>

      <WizardBottomBar progress={0}>
        <WizardBottomBarSpacer />
        <WizardBottomBarSection>
          <ButtonGroup>
            <Button
              to={links.measureOneSelectExperienceType(taxVm.job.id)}
              variant="outline"
              label="Add another account"
            />
            <WizardBottomBarPrimaryButton
              onPress={onPress}
              isPending={measureOneVm.importSelectedDocuments.pending}
              isDisabled={!measureOneVm.canImportDocuments}
              label="Import selected documents"
            />
          </ButtonGroup>
        </WizardBottomBarSection>
      </WizardBottomBar>
    </>
  )
})

function SelectDocumentsHeader() {
  return (
    <div className={styles.content}>
      <MeasureOneKicker />

      <WizardContentHeaderTitle>
        Review and select your documents
      </WizardContentHeaderTitle>
      <WizardContentHeaderDescription>
        Choose the documents you want to import
      </WizardContentHeaderDescription>
    </div>
  )
}

interface SelectDocumentsProps {
  requiredDocuments: WellKnownDocumentTypeShortId[]
  availableItems: AvailableItem[] | undefined
  onSelectItem: (item: AvailableItem) => void
  selectedItems: Set<Id>
  isLoading: boolean
}

function SelectDocumentsContainer({
  requiredDocuments,
  availableItems,
  onSelectItem,
  selectedItems,
  isLoading,
}: SelectDocumentsProps) {
  return (
    <>
      <JobRequiredDocuments requiredDocuments={requiredDocuments} />

      {isLoading ? (
        <MeasureOneSpinner />
      ) : (
        <AvailableItemsContainer
          availableItems={availableItems}
          onSelect={onSelectItem}
          selectedItems={selectedItems}
        />
      )}
    </>
  )
}

interface JobRequiredDocumentsProps {
  requiredDocuments: string[]
}

function JobRequiredDocuments({
  requiredDocuments,
}: JobRequiredDocumentsProps) {
  return (
    <div className={styles.documents}>
      <h2 className={styles.title}>Needed for your return</h2>

      <div className={styles.chips}>
        {requiredDocuments.map((document) => (
          <LabelChip key={document} text={document.replace('_', '-')} />
        ))}
      </div>
    </div>
  )
}

export interface AvailableItem extends MeasureItem {
  source: string
  dataRequestId: Id
}

interface AvailableItemsProps {
  availableItems: AvailableItem[] | undefined
  onSelect: (item: AvailableItem) => void
  selectedItems: Set<Id>
}

const AvailableItemsContainer = observer(function AvailableItems({
  availableItems,
  onSelect,
  selectedItems,
}: AvailableItemsProps) {
  const isSpacious = useIsSpacious()

  return (
    <AnswerOptions
      mode="multi"
      selectedKeys={selectedItems as Set<string>}
      layout={isSpacious ? 'grid' : 'stack'}
      items={availableItems}
    >
      {availableItems?.map((item) => (
        <AnswerOption
          onPress={() => onSelect(item)}
          key={item.id}
          id={item.id}
          label={item.sourceDataType.replace('_', '-')}
          description={item.source}
        />
      ))}
    </AnswerOptions>
  )
})
